import { forwardRef } from "react";

export type ProfileImageSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

interface ProfileImageUser {
  firstName?: string | null;
  lastName?: string | null;
  profileImageUrl?: string | null;
}

interface Props {
  user: ProfileImageUser | null | undefined;
  size?: ProfileImageSize;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  alt?: string;
  ariaLabel?: string;
}

const SIZE_CLASSES: Record<ProfileImageSize, { box: string; text: string }> = {
  xs: { box: "w-6 h-6", text: "text-xs" },
  sm: { box: "w-8 h-8", text: "text-sm" },
  md: { box: "w-10 h-10", text: "text-sm" },
  lg: { box: "w-16 h-16", text: "text-2xl" },
  xl: { box: "w-24 h-24", text: "text-3xl" },
  xxl: { box: "w-40 h-40", text: "text-3xl" },
};

function initials(user: ProfileImageUser | null | undefined): string {
  const first = user?.firstName?.trim()?.charAt(0) ?? "";
  const last = user?.lastName?.trim()?.charAt(0) ?? "";
  const combined = `${first}${last}`.toUpperCase();
  return combined || "?";
}

// Saturated gradient palettes for the no-image fallback. Static class strings
// so Tailwind's JIT picks them up at build time. White text is legible on all
// of them.
const FALLBACK_PALETTES = [
  "bg-gradient-to-br from-rose-500 to-pink-600",
  "bg-gradient-to-br from-orange-500 to-amber-600",
  "bg-gradient-to-br from-amber-500 to-red-500",
  "bg-gradient-to-br from-emerald-500 to-teal-600",
  "bg-gradient-to-br from-cyan-500 to-blue-600",
  "bg-gradient-to-br from-sky-500 to-indigo-600",
  "bg-gradient-to-br from-violet-500 to-fuchsia-600",
  "bg-gradient-to-br from-fuchsia-500 to-pink-600",
  "bg-gradient-to-br from-lime-500 to-emerald-600",
  "bg-gradient-to-br from-indigo-500 to-purple-600",
] as const;

// djb2 hash — small, deterministic, no deps. Good enough for bucket selection.
function hashName(user: ProfileImageUser | null | undefined): number {
  const key = `${user?.firstName ?? ""}${user?.lastName ?? ""}`.toLowerCase().trim();
  if (!key) return 0;
  let h = 5381;
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function paletteFor(user: ProfileImageUser | null | undefined): string {
  return FALLBACK_PALETTES[hashName(user) % FALLBACK_PALETTES.length];
}

export const ProfileImage = forwardRef<HTMLElement, Props>(function ProfileImage(
  { user, size = "md", onClick, className = "", alt, ariaLabel },
  ref,
) {
  const { box, text } = SIZE_CLASSES[size];
  const src = user?.profileImageUrl || null;
  const interactive = !!onClick;

  const baseClasses = `${box} rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden font-semibold select-none`;
  const interactiveClasses = interactive
    ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
    : "";
  const fallbackClasses = src ? "" : `${paletteFor(user)} text-white`;

  const content = src ? (
    <img
      src={src}
      alt={alt ?? user?.firstName ?? "Profile"}
      className={`${box} rounded-full object-cover`}
      draggable={false}
    />
  ) : (
    <span className={text}>{initials(user)}</span>
  );

  const merged = `${baseClasses} ${fallbackClasses} ${interactiveClasses} ${className}`.trim();

  if (interactive) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? "Profile picture"}
        className={merged}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      aria-label={ariaLabel}
      className={merged}
    >
      {content}
    </span>
  );
});
